/**
 * 자유 놀이터 교사 꾸밈(2026-10-11) — 운영 무대 꾸밈 인벤토리와 같은 품목(소품 · 캐릭터 · 물건 · 올린 모델)을 놀이터 바닥에 놓는다.
 * 보기 전용(기계가 지나간다) — 물리 · 판정 · 리플레이 해시와 무관. 룸 서버는 meta.mode.decor 로 그대로 실어 나르고 학생 · 칠판 룸 화면이 그린다.
 *  - 초안은 화면에만 있고 '✓ 배치 끝'에 물건과 함께 보낸다(POST …/layout {objects, decor})
 *  - 자리 = 놀이터 바닥(24 × 20 m · 가운데 z 2) · 0.5 m 격자 · 상한 = 무대 꾸밈과 같다(소품 40 · 손님 12 · 모델 8)
 */
import { CHARACTER_KINDS, ITEM_KINDS, PROP_KINDS } from "@picnic-field/renderer/stageDecor";
import type { DecorPreviewItem } from "@picnic-field/renderer";
import { FLOOR, SNAP_M } from "./sandbox";

export type DecorCat = "props" | "chars" | "items" | "models";
export const DECOR_CATS: readonly DecorCat[] = ["props", "chars", "items", "models"];

export interface DecorPiece {
  readonly id: string;
  readonly cat: DecorCat;
  /** 소품 종류 · 캐릭터 · 물건 · 모델 키 */
  readonly key: string;
  /** 바닥 자리 [x, z](m) */
  readonly at: readonly [number, number];
  readonly rot?: 0 | 90 | 180 | 270;
}

/** 놓을 수 있는 품목(만국기는 끝점이 필요해 빼고 · 모델은 카탈로그에서) */
export const DECOR_KINDS: Record<Exclude<DecorCat, "models">, readonly string[]> = {
  props: PROP_KINDS.filter((k) => k !== "bunting"),
  chars: CHARACTER_KINDS,
  items: ITEM_KINDS,
};
/** 상한(렌더러 STAGE_DECOR_LIMITS · 룸 서버 SANDBOX_DECOR_LIMITS) — 캐릭터와 물건은 손님(figures) 하나로 센다 */
export const DECOR_LIMITS = { props: 40, figures: 12, models: 8 } as const;
/** 손님 키(m) — 운영 꾸밈 편집기의 기본과 같다 */
export const FIGURE_SIZE = { chars: 0.4, items: 0.3 } as const;

const limitKey = (cat: DecorCat) =>
  cat === "props" ? "props" : cat === "models" ? "models" : "figures";
export function countOf(pieces: readonly DecorPiece[], cat: DecorCat): number {
  const k = limitKey(cat);
  return pieces.filter((p) => limitKey(p.cat) === k).length;
}
export function canAdd(pieces: readonly DecorPiece[], cat: DecorCat): boolean {
  return countOf(pieces, cat) < DECOR_LIMITS[limitKey(cat)];
}

const snap = (v: number) => Math.round(v / SNAP_M) * SNAP_M;
/** 격자에 맞추고 바닥 안으로 */
export function decorAt(x: number, z: number): [number, number] {
  const hx = FLOOR.x / 2;
  const hz = FLOOR.z / 2;
  return [
    Math.min(hx, Math.max(-hx, snap(x))),
    Math.min(FLOOR.cz + hz, Math.max(FLOOR.cz - hz, snap(z))),
  ];
}

const nextId = (pieces: readonly DecorPiece[]) =>
  `d${Math.max(0, ...pieces.map((p) => Number(/^d(\d+)$/.exec(p.id)?.[1] ?? 0))) + 1}`;

/** 놓기 — 상한이면 그대로(같은 목록) */
export function addPiece(
  pieces: readonly DecorPiece[],
  cat: DecorCat,
  key: string,
  x: number,
  z: number,
): readonly DecorPiece[] {
  if (!canAdd(pieces, cat)) return pieces;
  return [...pieces, { id: nextId(pieces), cat, key, at: decorAt(x, z) }];
}
export function movePiece(pieces: readonly DecorPiece[], id: string, x: number, z: number) {
  return pieces.map((p) => (p.id === id ? { ...p, at: decorAt(x, z) } : p));
}
export function rotatePiece(pieces: readonly DecorPiece[], id: string) {
  return pieces.map((p) =>
    p.id === id ? { ...p, rot: (((p.rot ?? 0) + 90) % 360) as 0 | 90 | 180 | 270 } : p,
  );
}
export function removePiece(pieces: readonly DecorPiece[], id: string) {
  return pieces.filter((p) => p.id !== id);
}

/** 서버 모양(RoomDecorData) — 모델 키(m)는 카탈로그 기본 키 · 없으면 0.5 */
export function decorPayload(
  pieces: readonly DecorPiece[],
  modelHeight: (key: string) => number | undefined = () => undefined,
): {
  props: Record<string, unknown>[];
  figures: Record<string, unknown>[];
  models: Record<string, unknown>[];
} {
  const rot = (p: DecorPiece) => (p.rot ? { rot: p.rot } : {});
  return {
    props: pieces
      .filter((p) => p.cat === "props")
      .map((p) => ({ kind: p.key, at: [p.at[0], p.at[1]], ...rot(p) })),
    figures: pieces
      .filter((p) => p.cat === "chars" || p.cat === "items")
      .map((p) => ({
        [p.cat === "chars" ? "char" : "item"]: p.key,
        at: [p.at[0], 0, p.at[1]],
        size: FIGURE_SIZE[p.cat as "chars" | "items"],
        // 손님이 놓인 방향을 바라보게(돌리기 90° 마다 · 0 = 교실 앞 +z)
        ...(p.rot
          ? {
              face: [
                p.at[0] + Math.round(Math.sin((p.rot * Math.PI) / 180) * 1000) / 1000,
                p.at[1] + Math.round(Math.cos((p.rot * Math.PI) / 180) * 1000) / 1000,
              ],
            }
          : {}),
      })),
    models: pieces
      .filter((p) => p.cat === "models")
      .map((p) => ({
        model: p.key,
        at: [p.at[0], 0, p.at[1]],
        size: modelHeight(p.key) ?? 0.5,
        ...rot(p),
      })),
  };
}

const num = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const rotOf = (v: unknown) =>
  v === 90 || v === 180 || v === 270 ? ({ rot: v } as const) : ({} as const);

/** meta.mode.decor(서버) → 초안 조각(모르는 모양은 뺀다 · id 는 화면용으로 다시 붙인다) */
export function readDecor(v: unknown): DecorPiece[] {
  const d = (v && typeof v === "object" ? v : {}) as Record<string, unknown>;
  const arr = (k: string) => (Array.isArray(d[k]) ? (d[k] as Record<string, unknown>[]) : []);
  const out: DecorPiece[] = [];
  const push = (cat: DecorCat, key: unknown, x: unknown, z: unknown, extra: object) => {
    if (typeof key !== "string" || !num(x) || !num(z)) return;
    out.push({ id: `d${out.length + 1}`, cat, key, at: [x, z], ...extra });
  };
  for (const p of arr("props")) {
    const at = Array.isArray(p.at) ? p.at : [];
    push("props", p.kind, at[0], at[1], rotOf(p.rot));
  }
  for (const f of arr("figures")) {
    const at = Array.isArray(f.at) ? f.at : [];
    const face = Array.isArray(f.face) ? f.face : null;
    const rot =
      face && num(face[0]) && num(face[1]) && num(at[0]) && num(at[2])
        ? (Math.round(((Math.atan2(face[0] - at[0], face[1] - at[2]) * 180) / Math.PI + 360) / 90) *
            90) %
          360
        : 0;
    push(
      typeof f.char === "string" ? "chars" : "items",
      f.char ?? f.item,
      at[0],
      at[2],
      rotOf(rot),
    );
  }
  for (const m of arr("models")) {
    const at = Array.isArray(m.at) ? m.at : [];
    push("models", m.model, at[0], at[2], rotOf(m.rot));
  }
  return out;
}

/** 두 꾸밈이 같은가(초안이 바뀌었나 — id 는 보지 않는다) */
export function sameDecor(a: readonly DecorPiece[], b: readonly DecorPiece[]): boolean {
  const key = (xs: readonly DecorPiece[]) =>
    JSON.stringify(xs.map((p) => [p.cat, p.key, p.at, p.rot ?? 0]).sort());
  return key(a) === key(b);
}

/** 미리보기 창 물체(모델은 받은 GLB 주소가 있어야 — 호출하는 쪽이 넣는다) */
export function decorPreview(
  cat: DecorCat,
  key: string,
  glbUrl?: string | null,
): DecorPreviewItem | null {
  if (cat === "props") return { prop: { kind: key as never, at: [0, 0] } };
  if (cat === "chars")
    return { figure: { char: key as never, at: [0, 0, 0], size: FIGURE_SIZE.chars } };
  if (cat === "items")
    return { figure: { item: key as never, at: [0, 0, 0], size: FIGURE_SIZE.items } };
  return glbUrl ? { glbUrl } : null;
}
